import {
  columnFilteringFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  rowExpandingFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
} from '@tanstack/react-table';
import type {
  AccessorKeyColumnDef,
  Cell,
  Column,
  ColumnDef,
  ColumnDefBase,
  Row,
  RowData,
  Table,
  TableOptions,
  Table_RowSelection,
} from '@tanstack/react-table';

/**
 * The feature set every table in the app registers. TanStack derives the shape of `Table`, `Row`, `Column` and friends
 * from the features a table was built with, so one shared object is what keeps those types interchangeable between the
 * generic Table component and the per-page column definitions handed to it.
 *
 * No row-model factory is registered: sorting and pagination are resolved server-side, and `expanded` carries the
 * row-checkbox state rather than nested rows, so every table renders the core row model.
 *
 * `columnFilteringFeature` is registered for its `enableColumnFilter` column option alone — the query builder reads it
 * to decide which properties it offers as filters. Filtering itself never runs here, since that needs a filtered row
 * model and none is registered.
 */
export const appTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowExpandingFeature,
  rowSelectionFeature,
  rowSortingFeature,
});

type AppTableFeatures = typeof appTableFeatures;

export type AppCell<TData extends RowData, TValue = unknown> = Cell<
  AppTableFeatures,
  TData,
  TValue
>;
export type AppColumn<TData extends RowData, TValue = unknown> = Column<
  AppTableFeatures,
  TData,
  TValue
>;
export type AppColumnDef<TData extends RowData, TValue = unknown> = ColumnDef<
  AppTableFeatures,
  TData,
  TValue
>;
export type AppColumnDefBase<
  TData extends RowData,
  TValue = unknown,
> = ColumnDefBase<AppTableFeatures, TData, TValue>;
export type AppAccessorKeyColumnDef<
  TData extends RowData,
  TValue = unknown,
> = AccessorKeyColumnDef<AppTableFeatures, TData, TValue>;
export type AppRow<TData extends RowData> = Row<AppTableFeatures, TData>;
export type AppTable<TData extends RowData> = Table<AppTableFeatures, TData>;
export type AppTableOptions<TData extends RowData> = TableOptions<
  AppTableFeatures,
  TData
>;
export type AppRowSelectionApi<TData extends RowData> = Table_RowSelection<
  AppTableFeatures,
  TData
>;

export const createAppColumnHelper = <TData extends RowData>() =>
  createColumnHelper<AppTableFeatures, TData>();
